import i18n, {Resource} from 'i18next';
import {initReactI18next} from 'react-i18next';
import yaml from 'js-yaml';

export enum SCREEN {
  MENU,
  SLIDE,
  TABLE,
  MAP,
  IMPRINT,
}

interface MenuScreenType {
  type: SCREEN.MENU;
  path: string;
  items: string[];
}

interface SlideScreenType {
  type: SCREEN.SLIDE;
  path: string;
  items: number;
}

interface TableScreenType {
  type: SCREEN.TABLE;
  path: string;
  items: number;
}

interface MapScreenType {
  type: SCREEN.MAP;
  path: string;
}

interface ImprintScreenType {
  type: SCREEN.IMPRINT;
  path: string;
}

export type ScreenType =
  | MenuScreenType
  | SlideScreenType
  | TableScreenType
  | MapScreenType
  | ImprintScreenType;

class Configuration {
  static readonly instance = new Configuration();

  /**
   * `Configuration` is a Singleton with a private constructor
   */
  private constructor() {}

  private config: any;
  private langs: string[] = ['de'];
  private _showLogos: true | string[] = [];

  async init() {
    const responses = await Promise.all([fetch('app.yml'), fetch('config.yml')]);
    const [cfgApp, cfg]: any[] = (
      await Promise.all([responses[0].text(), responses[1].text()])
    ).map(r => yaml.load(r));

    const ls = cfgApp.languages;
    if (Array.isArray(ls) && ls.length > 0) this.langs = ls.map(l => l.toLowerCase());
    this.config = cfg;

    const sl = cfgApp.showLogos;
    if (sl === true) this._showLogos = true;
    else if (Array.isArray(sl)) this._showLogos = sl;
    else this._showLogos = [];

    const resources: Resource = {};
    for (const l of this.langs) {
      resources[l] = {
        translation:
          l === 'de' ? cfg : (yaml.load(await (await fetch('config.' + l + '.yml')).text()) as any),
      };
    }

    await i18n.use(initReactI18next).init({
      resources,
      lng: this.langs[0],
      fallbackLng: false,
      interpolation: {escapeValue: false},
      react: {useSuspense: false},
    });
  }

  get languages() {
    return this.langs;
  }

  get showLogos() {
    return this._showLogos;
  }

  get idleTimeout() {
    return Math.floor((this.config.$idleTimeout || 0) * 60000);
  }

  get screenTypes() {
    const getScreenType = (path: string): ScreenType | null => {
      path = path.replace(/(\/)+$/g, '') + '/';
      if (!path) path = '/';

      let cfg = this.config;
      for (const key of path.split('/').filter(k => !!k)) {
        cfg = cfg?.[key];
        if (!cfg || key.startsWith('$')) return null;
      }

      const s = String(cfg.$screen);

      if (path === '/impressum/') {
        return {type: SCREEN.IMPRINT, path};
      } else if (s === 'map') {
        return {type: SCREEN.MAP, path};
      } else if (s.includes('slide')) {
        const items = parseInt(s.replace(/slide(s)?/, ''), 10);
        return {type: SCREEN.SLIDE, path, items};
      } else if (s.includes('card')) {
        const items = parseInt(s.replace(/card(s)?/, ''), 10);
        return {type: SCREEN.TABLE, path, items};
      } else {
        const items: string[] = Object.entries(cfg)
          .filter(([item, v]) => !item.startsWith('$') && !!v && v.constructor === Object)
          .map(([k, _]) => path + k);
        return {type: SCREEN.MENU, path, items};
      }
    };

    const addTypes = (path: string, types: ScreenType[]) => {
      const t = getScreenType(path);
      if (t) {
        types.push(t);
        if (t.type === SCREEN.MENU) for (const item of t.items) addTypes(item, types);
      }
    };

    const types: ScreenType[] = [];
    addTypes('/', types);
    return types;
  }

  assetsPath(path: string): string {
    if (!path.startsWith('/')) path = '/' + path;
    if (!path.endsWith('/')) path += '/';
    return 'assets' + path;
  }

  path2langKey(path: string, key: string) {
    path = path.replace(/^(\/)+/, '');
    if (path && !path.endsWith('/')) path += '/';
    return path.replace(/\//g, '.') + key;
  }
}
const configuration = Configuration.instance;
export default configuration;
